/**
 * Unpaid-member / explorer self-described segments.
 * Stored values are stable for badges, upgrades, and API checks.
 *
 * Canonical (post merge of "Which best describes you?" + "Stage of Business"):
 *   early_stage_entrepreneur
 *   small_to_medium_entrepreneur
 *   established_executive
 *
 * Legacy values are still recognized via normalizeExplorerSegment().
 */
export const EXPLORER_SEGMENT_EARLY_STAGE = "early_stage_entrepreneur";
export const EXPLORER_SEGMENT_SME = "small_to_medium_entrepreneur";
export const EXPLORER_SEGMENT_ESTABLISHED = "established_executive";

/** @deprecated Prefer EXPLORER_SEGMENT_EARLY_STAGE */
export const EXPLORER_SEGMENT_18_23 = "18_23_early_entrepreneur_sme";
/** @deprecated Prefer EXPLORER_SEGMENT_SME */
export const EXPLORER_SEGMENT_23_PLUS_EARLY = "23_plus_early_entrepreneur_sme";
/** @deprecated Prefer EXPLORER_SEGMENT_ESTABLISHED */
export const EXPLORER_SEGMENT_23_PLUS_ESTABLISHED =
  "23_plus_established_sme_1million_plus_turnover";

export const EXPLORER_SEGMENT_OPTIONS = [
  {
    value: EXPLORER_SEGMENT_EARLY_STAGE,
    label: "Early Stage Entrepreneur",
  },
  {
    value: EXPLORER_SEGMENT_SME,
    label: "Small to Medium Entrepreneur",
  },
  {
    value: EXPLORER_SEGMENT_ESTABLISHED,
    label: "Established/Executive",
  },
] as const;

export type ExplorerSegmentValue =
  (typeof EXPLORER_SEGMENT_OPTIONS)[number]["value"];

const LEGACY_TO_CANONICAL: Record<string, ExplorerSegmentValue> = {
  [EXPLORER_SEGMENT_18_23]: EXPLORER_SEGMENT_EARLY_STAGE,
  [EXPLORER_SEGMENT_23_PLUS_EARLY]: EXPLORER_SEGMENT_SME,
  [EXPLORER_SEGMENT_23_PLUS_ESTABLISHED]: EXPLORER_SEGMENT_ESTABLISHED,
  // Old Stage of Business labels (fallback when segment blank)
  "Early Stage": EXPLORER_SEGMENT_EARLY_STAGE,
  "Early Stage Entrepreneur": EXPLORER_SEGMENT_EARLY_STAGE,
  Growth: EXPLORER_SEGMENT_SME,
  "Small to Medium Entrepreneur": EXPLORER_SEGMENT_SME,
  Established: EXPLORER_SEGMENT_ESTABLISHED,
  Executive: EXPLORER_SEGMENT_ESTABLISHED,
  "Established/Executive": EXPLORER_SEGMENT_ESTABLISHED,
};

const ALLOWED = new Set<string>([
  ...EXPLORER_SEGMENT_OPTIONS.map((option) => option.value),
  ...Object.keys(LEGACY_TO_CANONICAL),
]);

/** Map legacy segment/stage strings to the canonical stored value. */
export function normalizeExplorerSegment(
  value: string | null | undefined,
): ExplorerSegmentValue | "" {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "";
  if (
    trimmed === EXPLORER_SEGMENT_EARLY_STAGE ||
    trimmed === EXPLORER_SEGMENT_SME ||
    trimmed === EXPLORER_SEGMENT_ESTABLISHED
  ) {
    return trimmed;
  }
  return LEGACY_TO_CANONICAL[trimmed] ?? "";
}

/** Human label written to StageOfBusiness for backward-compatible discovery/event readers. */
export function stageLabelForExplorerSegment(
  value: string | null | undefined,
): string {
  const canonical = normalizeExplorerSegment(value);
  const opt = EXPLORER_SEGMENT_OPTIONS.find((o) => o.value === canonical);
  return opt?.label ?? "";
}

export function isAllowedExplorerSegment(
  value: string | null | undefined,
): boolean {
  return !!value?.trim() && ALLOWED.has(value.trim());
}

export function isExplorerEighteenToTwentyThreeSegment(
  value: string | null | undefined,
): boolean {
  const canonical = normalizeExplorerSegment(value);
  // Age bucket removed from UI; treat early-stage (incl. legacy 18–23) the same.
  return (
    canonical === EXPLORER_SEGMENT_EARLY_STAGE ||
    (value ?? "").trim() === EXPLORER_SEGMENT_18_23
  );
}
